import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Identifier, Schedule } from '@medplum/fhirtypes';
import type { AppointmentCategory } from '../features/calendar/saveCalendarAppointment';

const SCHEDULE_APPOINTMENT_BOT_IDENTIFIER: Identifier = {
  system: 'https://unify.health/bots',
  value: 'schedule-appointment',
};

function getBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_SCHEDULE_APPOINTMENT_BOT_ID?.trim();
  if (id) return id;
  return {
    system:
      import.meta.env.VITE_MEDPLUM_SCHEDULE_APPOINTMENT_BOT_IDENTIFIER_SYSTEM?.trim()
      ?? SCHEDULE_APPOINTMENT_BOT_IDENTIFIER.system,
    value:
      import.meta.env.VITE_MEDPLUM_SCHEDULE_APPOINTMENT_BOT_IDENTIFIER_VALUE?.trim()
      ?? SCHEDULE_APPOINTMENT_BOT_IDENTIFIER.value,
  };
}

export interface ScheduledAppointmentArgs {
  start: Date;
  end: Date;
  calendarStatus: string;
  practitionerId: string;
  practitionerDisplay?: string;
  siteId?: string;
  roomId?: string;
  deviceIds?: string[];
  organizationId?: string;
  patientId?: string;
  patientDisplay?: string;
  episodeOfCareId?: string;
  cptCode?: string;
  cptDisplay?: string;
  serviceTypeCode?: string;
  serviceTypeDisplay?: string;
  serviceTypeSystem?: string;
  /** HealthcareService id for clinical session/group appointments (catalog round-trip). */
  sessionHealthcareServiceId?: string;
  /** HealthcareService.name shown as the appointment's Session Type. */
  sessionHealthcareServiceDisplay?: string;
  appointmentCategory?: AppointmentCategory;
  modality?: string;
  description?: string;
  videoLink?: string;
  recurringPattern?: string;
  additionalParticipants?: Array<{
    resourceType: 'Practitioner' | 'Patient' | 'RelatedPerson';
    id: string;
    display?: string;
  }>;
  therapyGroupId?: string;
  therapyGroupDisplay?: string;
  /** Keep Patient/Practitioner participants the scheduling form does not render. */
  preserveExistingParticipants?: boolean;
}

export async function stampProviderScheduleAccounts(
  medplum: MedplumClient,
  scheduleId: string,
  practitionerId: string,
): Promise<Schedule> {
  return medplum.executeBot(
    getBotTarget(),
    { action: 'stamp-provider-schedule-accounts', scheduleId, practitionerId },
    ContentType.JSON,
  ) as Promise<Schedule>;
}

export async function createScheduledAppointment(
  medplum: MedplumClient,
  args: ScheduledAppointmentArgs,
): Promise<Appointment> {
  return medplum.executeBot(
    getBotTarget(),
    { action: 'create-appointment', ...args },
    ContentType.JSON,
  ) as Promise<Appointment>;
}

export async function updateScheduledAppointment(
  medplum: MedplumClient,
  appointmentId: string,
  args: ScheduledAppointmentArgs,
): Promise<Appointment> {
  return medplum.executeBot(
    getBotTarget(),
    { action: 'update-appointment', appointmentId, ...args },
    ContentType.JSON,
  ) as Promise<Appointment>;
}
